import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { TaskEditorState } from "../utils/taskEditorUtils";
import { cloneTaskEditorState } from "../utils/taskEditorDiff";
import { resumeTaskAuthoring, streamTaskAuthoring } from "./apiClient";
import {
  clearChat,
  loadLatestChat,
  loadSpecificChat,
  persistTurn,
} from "./chatStorage";
import type {
  AssistantStep,
  AssistantTurn,
  ChatTurn,
  Decision,
  SSEEvent,
  TaskBlocksPatch,
  UserTurn,
} from "./types";

// 이벤트가 한 건도 안 오는 서버 hang 을 끊는 idle 타임아웃.
const IDLE_TIMEOUT_MS = 60_000;
// final/error 수신 후 trailing debug 이벤트를 잠깐 기다렸다가 연결을 정리하는 grace.
const POST_FINAL_GRACE_MS = 2_000;

const rid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const generateThreadId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return rid() + "-" + rid();
};

// 활동지(taskId) 한 개의 채팅 세션. AgentChatPanel 은 활동지/스레드를 전환해도
// 리마운트되지 않으므로(부모가 key 를 안 줌) 이 hook 인스턴스 하나가 모든 활동지의
// 세션을 동시에 들고 있는다. 화면에는 활성 taskId 의 세션만 노출하지만, 비활성
// 활동지의 스트림도 백그라운드에서 계속 진행된다(전환해도 abort 안 함).
interface AgentSession {
  threadId: string | null;
  turns: ChatTurn[];
  isStreaming: boolean;
  // BE 에서 thread 내역을 가져오는 중 — 해당 활동지 첫 진입/스레드 전환 시.
  isLoadingHistory: boolean;
}

const EMPTY_SESSION: AgentSession = {
  threadId: null,
  turns: [],
  isStreaming: false,
  isLoadingHistory: false,
};

interface UseAgentChatThreadResult {
  threadId: string | null;
  turns: ChatTurn[];
  isStreaming: boolean;
  // 현재 스트리밍 중인 모든 활동지 id (활성/백그라운드 포함) — 목록 인디케이터용.
  streamingTaskIds: string[];
  // BE 에서 thread 내역을 가져오는 동안 true — 초기 진입/활동지 전환/thread 전환 시.
  isLoadingHistory: boolean;
  send: (message: string, currentTask?: TaskEditorState) => Promise<void>;
  // 마지막 user turn 을 수정해 재실행 — 활동지를 그 직전(taskBefore)으로 되돌리고
  // checkpoint 절단(rewind) 후 같은 turn 슬롯으로 재전송.
  editLastUserTurn: (
    newMessage: string,
    currentTask?: TaskEditorState
  ) => Promise<void>;
  respondToApproval: (
    turnId: string,
    decisions: Decision[],
    currentTask?: TaskEditorState
  ) => Promise<void>;
  clear: () => void;
  cancel: () => void;
  // 턴 단위 revert UI 에서 사용 — taskBefore 가 저장된 턴을 찾아 호출자가 적용한다.
  revertTurn: (turnId: string) => TaskEditorState | undefined;
  // 이전 대화 드롭다운에서 다른 thread 로 swap. 진행 중이면 abort 후 swap.
  switchThread: (targetThreadId: string) => Promise<void>;
}

interface UseAgentChatThreadArgs {
  // 턴 시작/종료 콜백 — sync state 의 markTurnStart/End 와 연결한다.
  onTurnStart?: () => void;
  onTurnEnd?: () => void;
  // 마지막 요청 수정 시 활동지를 taskBefore 로 복원하고 working_store PUT 완료까지 await —
  // rewind 스트림이 복원본을 읽도록 재실행 전 순서를 보장한다.
  revertTaskBefore?: (taskBefore: TaskEditorState) => Promise<void>;
}

export function useAgentChatThread(
  taskId: string | undefined,
  onTaskPatch: (
    patch: TaskBlocksPatch,
    meta?: { version?: number; revision?: string }
  ) => void,
  args: UseAgentChatThreadArgs = {}
): UseAgentChatThreadResult {
  // taskId 별 세션 맵. 화면 표시는 활성 taskId 의 세션만 쓰고, 나머지는 백그라운드.
  const [sessions, setSessions] = useState<Record<string, AgentSession>>({});
  // 스트림 콜백 등 비동기 클로저에서 최신 세션을 동기적으로 읽기 위한 미러.
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  // 엔진 콜백(onTaskPatch/onTurnStart/onTurnEnd)은 "활성 에디터" 1개를 조작하므로,
  // 백그라운드 활동지의 스트림에서는 호출하지 않는다. 활성 taskId 를 ref 로 비교.
  const activeTaskIdRef = useRef(taskId);
  activeTaskIdRef.current = taskId;

  // 비반응 상태(렌더 트리거 불필요) — taskId 별로 분리 보관.
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const persistSignaturesRef = useRef<Map<string, Map<string, string>>>(
    new Map()
  );
  const persistTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  // 한 번 BE 에서 로드한 활동지는 다시 reset/reload 하지 않는다 — 진행 중인 백그라운드
  // 세션을 덮어쓰지 않도록 lazy load.
  const loadedTasksRef = useRef<Set<string>>(new Set());

  const onTaskPatchRef = useRef(onTaskPatch);
  const onTurnStartRef = useRef(args.onTurnStart);
  const onTurnEndRef = useRef(args.onTurnEnd);
  const revertTaskBeforeRef = useRef(args.revertTaskBefore);

  useEffect(() => {
    onTaskPatchRef.current = onTaskPatch;
  }, [onTaskPatch]);

  useEffect(() => {
    onTurnStartRef.current = args.onTurnStart;
    onTurnEndRef.current = args.onTurnEnd;
    revertTaskBeforeRef.current = args.revertTaskBefore;
  }, [args.onTurnStart, args.onTurnEnd, args.revertTaskBefore]);

  // 세션 부분 갱신 헬퍼.
  const patchSession = useCallback(
    (tid: string, partial: Partial<AgentSession>) => {
      setSessions((prev) => ({
        ...prev,
        [tid]: { ...EMPTY_SESSION, ...prev[tid], ...partial },
      }));
    },
    []
  );
  const updateTurns = useCallback(
    (tid: string, fn: (turns: ChatTurn[]) => ChatTurn[]) => {
      setSessions((prev) => {
        const cur = prev[tid] ?? EMPTY_SESSION;
        return { ...prev, [tid]: { ...cur, turns: fn(cur.turns) } };
      });
    },
    []
  );
  const setStreaming = useCallback((tid: string, value: boolean) => {
    setSessions((prev) => {
      const cur = prev[tid] ?? EMPTY_SESSION;
      if (cur.isStreaming === value && prev[tid]) return prev;
      return { ...prev, [tid]: { ...cur, isStreaming: value } };
    });
  }, []);

  // taskId 첫 진입 시 BE 에서 가장 최근(active) thread 의 turns 를 가져온다 (network).
  // 이미 로드한(또는 백그라운드 스트리밍 중인) 활동지는 건드리지 않는다 — 라이브 세션
  // 보존. race: 마운트 도중 결과가 와도 cancelled 가드로 stale 무시.
  // useLayoutEffect 인 이유: 첫 진입 프레임에서 turns=[] + loading=true 가 paint 전에
  // 커밋돼야 EmptyState(시나리오 버튼) 깜빡임이 없다.
  useLayoutEffect(() => {
    if (!taskId) return;
    if (loadedTasksRef.current.has(taskId)) return;
    loadedTasksRef.current.add(taskId);
    const tid = taskId;
    patchSession(tid, {
      turns: [],
      threadId: null,
      isLoadingHistory: true,
    });
    let cancelled = false;
    void loadLatestChat(tid).then((loaded) => {
      if (cancelled) return;
      if (loaded) {
        // BE 에서 막 받아온 turn 은 이미 sync 상태. persist effect 가 이걸 다시 PUT 하면
        // BE 가 turn 마다 thread.updated_at 을 NOW 로 갱신해 시간 표시가 매번 "방금" 으로
        // 갱신됨. 로드한 turn 들의 signature 를 미리 등록해 skip 시킨다.
        const sigs = new Map<string, string>();
        loaded.turns.forEach((t) => sigs.set(t.id, JSON.stringify(t)));
        persistSignaturesRef.current.set(tid, sigs);
        patchSession(tid, {
          threadId: loaded.threadId,
          turns: loaded.turns,
          isLoadingHistory: false,
        });
      } else {
        patchSession(tid, { isLoadingHistory: false });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [taskId, patchSession]);

  // turn 추가/변경 시 BE 에 upsert. localStorage 와 달리 fire-and-forget — 실패는 콘솔만,
  // UI 는 그대로 (best-effort). sequence = 배열 index + 1 (1-based, BE 요구).
  //
  // 무차별 매 이벤트마다 PUT 보내면 SSE 토큰 chunk 마다 네트워크 부담 → 500ms debounce.
  // signature (JSON) 캐시로 unchanged turn 은 PUT 생략. 활동지(taskId)별로 독립 debounce.
  useEffect(() => {
    const timers = persistTimersRef.current;
    Object.entries(sessions).forEach(([tid, session]) => {
      if (!session.threadId) return;
      const threadId = session.threadId;
      const turns = session.turns;
      const existing = timers.get(tid);
      if (existing) clearTimeout(existing);
      timers.set(
        tid,
        setTimeout(() => {
          let sigs = persistSignaturesRef.current.get(tid);
          if (!sigs) {
            sigs = new Map<string, string>();
            persistSignaturesRef.current.set(tid, sigs);
          }
          turns.forEach((turn, index) => {
            const sig = JSON.stringify(turn);
            if (sigs!.get(turn.id) === sig) return;
            sigs!.set(turn.id, sig);
            // fire-and-forget — 실패해도 다음 변경 시 재시도된다 (signature 가 다르므로).
            void persistTurn(threadId, tid, turn, index + 1);
          });
        }, 500)
      );
    });
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, [sessions]);

  // assistantTurn 갱신 reducer. task_patch 는 활성 활동지의 스트림일 때만 에디터로
  // 전달한다 — 백그라운드 활동지는 BE 가 working_store 에 persist 하고, 사용자가 복귀할 때
  // 에디터 하이드레이트가 그 결과를 읽어 반영한다.
  const applyEvent = useCallback(
    (tid: string, turnId: string, event: SSEEvent) => {
      // keepalive 는 idle 타이머 리셋(consumeStream 의 armIdle)이 유일한 목적 —
      // turn state 변화가 없으므로 setSessions 자체를 호출하지 않는다.
      if (event.event === "keepalive") return;
      updateTurns(tid, (prev) =>
        prev.map((turn) => {
          if (turn.role !== "assistant" || turn.id !== turnId) return turn;
          return reduceAssistantTurn(turn, event);
        })
      );
      if (event.event === "task_patch" && tid === activeTaskIdRef.current) {
        // wire 는 저장 envelope(content.blocks) — 내부 patch 단위 {blocks} 로 unnest.
        onTaskPatchRef.current(
          { blocks: event.data.task.content.blocks },
          {
            version: event.data.version,
            revision: event.data.revision,
          }
        );
      }
    },
    [updateTurns]
  );

  const consumeStream = useCallback(
    async (
      tid: string,
      turnId: string,
      stream: AsyncGenerator<SSEEvent>,
      controller: AbortController
    ) => {
      const signal = controller.signal;
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      let timedOut = false;
      let finalized = false;

      // 이벤트가 올 때마다 idle 타이머 리셋. 만료되면 turn 을 timeout 에러로
      // 표시하고 연결을 끊는다. (abort 시 제너레이터는 throw 없이 정상 종료되므로
      // 에러 표시는 여기서 직접 한다 — catch 에 의존하지 않음.)
      const armIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          timedOut = true;
          updateTurns(tid, (prev) =>
            prev.map((turn) =>
              turn.role === "assistant" &&
              turn.id === turnId &&
              turn.status === "streaming"
                ? {
                    ...turn,
                    status: "error",
                    error: {
                      type: "timeout",
                      message: "응답 시간이 초과됐어요. 다시 시도해 주세요.",
                    },
                  }
                : turn
            )
          );
          setStreaming(tid, false);
          controller.abort();
        }, IDLE_TIMEOUT_MS);
      };

      try {
        armIdle();
        for await (const event of stream) {
          // finalized 후엔 grace 타이머가 연결 정리를 담당하므로 idle 재무장 안 함.
          if (!finalized) armIdle();
          applyEvent(tid, turnId, event);
          // abort(정지/타임아웃/grace) 후에도 postSSE 가 flush 한 잔여 이벤트는 적용하고 종료한다.
          // 적용 전에 break 하면 정지 직전 수신된(BE 에 이미 commit 된) task_patch 가 유실돼
          // 우측 패널이 stale 로 남는다 — abort 체크를 applyEvent 뒤로 둔다.
          if (signal.aborted) break;
          if (event.event === "final" || event.event === "error") {
            // 논리적 종료 — 스피너 즉시 해제. 연결은 trailing debug 를 위해
            // 짧은 grace 뒤 정리한다 (서버가 close 안 해도 무한 대기 방지).
            finalized = true;
            setStreaming(tid, false);
            if (tid === activeTaskIdRef.current) onTurnEndRef.current?.();
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(
              () => controller.abort(),
              POST_FINAL_GRACE_MS
            );
          }
        }
      } catch (err) {
        // abort(타임아웃/사용자취소/grace)·정상종료는 throw 하지 않는다.
        // 여기 오는 건 진짜 네트워크/스트림 오류뿐. timeout 은 armIdle 에서 이미 처리.
        if (!finalized && !timedOut && !signal.aborted) {
          const message = err instanceof Error ? err.message : String(err);
          updateTurns(tid, (prev) =>
            prev.map((turn) =>
              turn.role === "assistant" && turn.id === turnId
                ? {
                    ...turn,
                    status: "error",
                    error: { type: "stream_error", message },
                  }
                : turn
            )
          );
        }
        // finally 에서도 turn end 콜백을 호출하는데, 정상 final/error 경로는 위에서 이미
        // 호출됐다. ref 호출 자체는 idempotent 하지만 markTurnEnd 가 중복돼도 안전 (clear-on-null).
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
        // 잔여 연결이 정리되는 사이 새 turn 이 시작됐다면 그쪽 스피너를 끄지 않는다.
        if (abortControllersRef.current.get(tid) === controller) {
          setStreaming(tid, false);
        }
        if (!finalized && tid === activeTaskIdRef.current) {
          onTurnEndRef.current?.();
        }
      }
    },
    [applyEvent, updateTurns, setStreaming]
  );

  const send = useCallback(
    async (message: string, currentTask?: TaskEditorState) => {
      const tid = taskId;
      if (!tid) return;
      const session = sessionsRef.current[tid] ?? EMPTY_SESSION;
      if (session.isStreaming) return;
      let threadId = session.threadId;
      if (!threadId) {
        threadId = generateThreadId();
      }
      const userTurn: UserTurn = {
        id: rid(),
        role: "user",
        content: message,
        createdAt: Date.now(),
      };
      // 턴 시작 시점의 task 를 snapshot — 추후 "이 턴 되돌리기" 의 복원 대상.
      const taskBefore = currentTask
        ? cloneTaskEditorState(currentTask)
        : undefined;
      const assistantTurn: AssistantTurn = {
        id: rid(),
        role: "assistant",
        steps: [],
        status: "streaming",
        taskChanged: false,
        taskBefore,
        createdAt: Date.now(),
      };
      patchSession(tid, {
        threadId,
        isStreaming: true,
      });
      updateTurns(tid, (prev) => [...prev, userTurn, assistantTurn]);

      // sync state 에 base 등록 — agent patch 도착 시 3-way merge 의 base 가 됨.
      // send 는 활성 활동지에서만 호출되므로 곧바로 호출 (tid === active).
      onTurnStartRef.current?.();

      // 직전 turn 의 잔여 연결(서버가 close 안 한 경우)을 정리하고 시작.
      abortControllersRef.current.get(tid)?.abort();
      const controller = new AbortController();
      abortControllersRef.current.set(tid, controller);

      // BE 가 working_task store 에서 task_id 의 blocks 를 fetch 해 vfs hydrate 한다.
      // current_task 송신 폐기 — task_id 만 보내면 BE 가 single source 로 작동.
      const stream = streamTaskAuthoring(
        {
          thread_id: threadId,
          message,
          task_id: currentTask?.task_id,
        },
        controller.signal
      );
      await consumeStream(tid, assistantTurn.id, stream, controller);
    },
    [consumeStream, patchSession, updateTurns, taskId]
  );

  const editLastUserTurn = useCallback(
    async (newMessage: string, currentTask?: TaskEditorState) => {
      const tid = taskId;
      if (!tid) return;
      const session = sessionsRef.current[tid] ?? EMPTY_SESSION;
      if (session.isStreaming || !session.threadId) return;
      const turns = session.turns;
      const last = turns[turns.length - 1];
      const prevUser = turns[turns.length - 2];
      // 마지막 turn 이 종료된 assistant 이고 그 직전이 user 일 때만 — streaming/awaiting_approval 중엔 불가.
      if (
        !last ||
        last.role !== "assistant" ||
        (last.status !== "complete" &&
          last.status !== "error" &&
          last.status !== "cancelled") ||
        !prevUser ||
        prevUser.role !== "user"
      ) {
        return;
      }
      const trimmed = newMessage.trim();
      if (!trimmed) return;

      // 1) 활동지를 그 요청 직전(taskBefore)으로 복원하고 working_store PUT 완료까지 대기 —
      //    BE rewind 스트림이 복원본 blocks 를 읽도록 순서 보장.
      if (last.taskBefore) {
        await revertTaskBeforeRef.current?.(last.taskBefore);
      }

      // 2) 마지막 user/assistant turn 을 같은 id 로 제자리 리셋(BE chat_turns 덮어쓰기 → 고아 없음).
      const assistantId = last.id;
      updateTurns(tid, (prev) =>
        prev.map((turn, index) => {
          if (index === prev.length - 2 && turn.role === "user") {
            return { ...turn, content: trimmed };
          }
          if (index === prev.length - 1 && turn.role === "assistant") {
            // id 와 taskBefore 유지 — 복원 후 task == taskBefore 라 재실행 base/diff anchor 로 그대로 유효.
            return {
              ...turn,
              steps: [],
              status: "streaming",
              taskChanged: false,
              todos: undefined,
              pendingApproval: undefined,
              debug: undefined,
              error: undefined,
            };
          }
          return turn;
        })
      );
      setStreaming(tid, true);
      onTurnStartRef.current?.();

      abortControllersRef.current.get(tid)?.abort();
      const controller = new AbortController();
      abortControllersRef.current.set(tid, controller);

      const stream = streamTaskAuthoring(
        {
          thread_id: session.threadId,
          message: trimmed,
          task_id: currentTask?.task_id,
          rewind_last_user_turn: true,
        },
        controller.signal
      );
      await consumeStream(tid, assistantId, stream, controller);
    },
    [consumeStream, updateTurns, setStreaming, taskId]
  );

  const respondToApproval = useCallback(
    async (
      turnId: string,
      decisions: Decision[],
      currentTask?: TaskEditorState
    ) => {
      const tid = taskId;
      if (!tid) return;
      const session = sessionsRef.current[tid] ?? EMPTY_SESSION;
      if (session.isStreaming || !session.threadId) return;
      // 결정 step 추가 + status streaming 으로 변경
      updateTurns(tid, (prev) =>
        prev.map((turn) => {
          if (turn.role !== "assistant" || turn.id !== turnId) return turn;
          if (!turn.pendingApproval) return turn;
          const actions = turn.pendingApproval.action_requests;
          const decisionSteps: AssistantStep[] = decisions.map(
            (decision, idx) => ({
              kind: "approval_decision",
              id: rid(),
              decision,
              originalAction: actions[idx] ?? actions[0],
            })
          );
          return {
            ...turn,
            steps: [...turn.steps, ...decisionSteps],
            status: "streaming",
            pendingApproval: undefined,
          };
        })
      );

      setStreaming(tid, true);
      // resume 은 같은 논리적 턴의 연속 — markTurnStart 는 호출하지 않는다 (base 유지).
      abortControllersRef.current.get(tid)?.abort();
      const controller = new AbortController();
      abortControllersRef.current.set(tid, controller);
      const stream = resumeTaskAuthoring(
        {
          thread_id: session.threadId,
          decisions,
          task_id: currentTask?.task_id,
        },
        controller.signal
      );
      await consumeStream(tid, turnId, stream, controller);
    },
    [consumeStream, updateTurns, setStreaming, taskId]
  );

  const clear = useCallback(() => {
    const tid = taskId;
    if (!tid) return;
    abortControllersRef.current.get(tid)?.abort();
    abortControllersRef.current.delete(tid);
    const session = sessionsRef.current[tid];
    const threadId = session?.threadId; // 현재 thread 만 soft delete. 새 thread 는 다음 send 가 발급.
    patchSession(tid, {
      threadId: null,
      turns: [],
      isStreaming: false,
    });
    if (tid === activeTaskIdRef.current) onTurnEndRef.current?.();
    persistSignaturesRef.current.get(tid)?.clear();
    if (threadId) void clearChat(threadId);
  }, [patchSession, taskId]);

  const cancel = useCallback(() => {
    const tid = taskId;
    if (!tid) return;
    abortControllersRef.current.get(tid)?.abort();
    abortControllersRef.current.delete(tid);
    setStreaming(tid, false);
    // 진행 중이던 assistant turn 을 cancelled 로 마감 — 부분 steps 는 그대로 유지.
    // (running tool step 은 turnActive=false 가 되어 정적 마커로 멈춘다 — turnView 의 기존 동작.)
    updateTurns(tid, (prev) =>
      prev.map((turn) =>
        turn.role === "assistant" && turn.status === "streaming"
          ? { ...turn, status: "cancelled" }
          : turn
      )
    );
    if (tid === activeTaskIdRef.current) onTurnEndRef.current?.();
  }, [setStreaming, updateTurns, taskId]);

  const revertTurn = useCallback(
    (turnId: string): TaskEditorState | undefined => {
      const tid = taskId;
      if (!tid) return undefined;
      const turns = sessionsRef.current[tid]?.turns ?? [];
      const found = turns.find(
        (t) => t.role === "assistant" && t.id === turnId
      );
      if (!found || found.role !== "assistant") return undefined;
      return found.taskBefore;
    },
    [taskId]
  );

  const switchThread = useCallback(
    async (targetThreadId: string) => {
      const tid = taskId;
      if (!tid) return;
      abortControllersRef.current.get(tid)?.abort();
      abortControllersRef.current.delete(tid);
      if (tid === activeTaskIdRef.current) onTurnEndRef.current?.();
      patchSession(tid, { isStreaming: false, isLoadingHistory: true });
      try {
        const loaded = await loadSpecificChat(targetThreadId);
        if (loaded) {
          // 로드한 turn 들의 signature 미리 등록 — persist effect 가 다시 PUT 해 BE 의
          // thread.updated_at 이 NOW 로 갱신되는 부작용 방지 (위 loadLatestChat 과 동일 이유).
          const sigs = new Map<string, string>();
          loaded.turns.forEach((t) => sigs.set(t.id, JSON.stringify(t)));
          persistSignaturesRef.current.set(tid, sigs);
          patchSession(tid, {
            threadId: loaded.threadId,
            turns: loaded.turns,
            isLoadingHistory: false,
          });
        } else {
          patchSession(tid, { isLoadingHistory: false });
        }
      } finally {
        patchSession(tid, { isLoadingHistory: false });
      }
    },
    [patchSession, taskId]
  );

  useEffect(() => {
    const controllers = abortControllersRef.current;
    return () => {
      controllers.forEach((c) => c.abort());
      controllers.clear();
    };
  }, []);

  const active = taskId ? sessions[taskId] : undefined;
  const streamingTaskIds = Object.keys(sessions).filter(
    (tid) => sessions[tid].isStreaming
  );
  return {
    threadId: active?.threadId ?? null,
    turns: active?.turns ?? [],
    isStreaming: active?.isStreaming ?? false,
    streamingTaskIds,
    // 세션 엔트리가 아직 없으면(첫 paint, 로드 effect 커밋 전) taskId 가 있는 한 로딩으로
    // 본다 — EmptyState(시나리오 버튼) 깜빡임 방지.
    isLoadingHistory: active ? active.isLoadingHistory : !!taskId,
    send,
    editLastUserTurn,
    respondToApproval,
    clear,
    cancel,
    revertTurn,
    switchThread,
  };
}

function reduceAssistantTurn(
  turn: AssistantTurn,
  event: SSEEvent
): AssistantTurn {
  switch (event.event) {
    case "session":
      return turn;

    case "tool_call": {
      const step: AssistantStep = {
        kind: "tool",
        id: rid(),
        callId: event.data.call_id,
        name: event.data.name,
        args: event.data.args,
        startedAt: Date.now(),
        status: "running",
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "tool_result": {
      const { name, preview, call_id: callId } = event.data;
      // 매칭할 running 도구 step 을 고른다.
      // 1) call_id(=BE run_id) 우선 — 같은 name 의 도구를 한 턴에 여러 번 호출하고 결과가
      //    호출 순서와 다르게 도착해도 올바른 step 에 붙는다 (파일명↔내용 어긋남 방지).
      // 2) call_id 가 없거나(구버전 BE) 매칭 step 이 없으면, 같은 name 의 "가장 먼저
      //    시작된(running)" step 으로 FIFO 폴백.
      const target =
        (callId != null &&
          turn.steps.find(
            (s) =>
              s.kind === "tool" && s.status === "running" && s.callId === callId
          )) ||
        turn.steps.find(
          (s) => s.kind === "tool" && s.status === "running" && s.name === name
        );
      if (!target) return turn;
      const nextSteps = turn.steps.map((step) =>
        step.id === target.id && step.kind === "tool"
          ? {
              ...step,
              status: "done" as const,
              result: preview,
              finishedAt: Date.now(),
            }
          : step
      );
      return { ...turn, steps: nextSteps };
    }

    case "message_chunk": {
      const last = turn.steps[turn.steps.length - 1];
      if (last?.kind === "text") {
        const merged: AssistantStep = {
          ...last,
          content: last.content + event.data.delta,
        };
        return {
          ...turn,
          steps: [...turn.steps.slice(0, -1), merged],
        };
      }
      const step: AssistantStep = {
        kind: "text",
        id: rid(),
        content: event.data.delta,
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "pending_approval":
      return {
        ...turn,
        status: "awaiting_approval",
        pendingApproval: event.data,
      };

    case "task_patch":
      return { ...turn, taskChanged: true };

    case "todo_update":
      // todo_update 는 매번 전체 목록 — append 아니라 치환.
      return { ...turn, todos: event.data.todos };

    case "final":
      return {
        ...turn,
        status: "complete",
        taskChanged: event.data.task_changed,
      };

    case "debug":
      return {
        ...turn,
        debug: {
          trace_id: event.data.trace_id,
          execution_time_ms: event.data.execution_time_ms,
        },
      };

    case "error":
      return {
        ...turn,
        status: "error",
        error: { type: event.data.type, message: event.data.message },
      };

    default:
      return turn;
  }
}
